import * as React from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'reactstrap';
import { Galleria } from "primereact/galleria";
import ExtLink from "../../../parts/ExtLink";
import { ProteinRow } from "../types/protein-row";
import ShowMoreText from "../../../commons/ShowMoreText";
import { addPubmedLinksIntoPlainText, joinReactNodes, pubmedLinkRanges } from "../../../utils/toolsReact";
import * as Messages from "../../../Messages";
import classNames from "classnames";
import { Element, scroller } from "react-scroll";
import { useCallback, useEffect, useState } from "react";
import { getApiUrl } from "../../../api";
import { ProteinControllerApi } from "../../../api/openapi-generator";

interface Props {
  dataRows: ProteinRow[],
  selected: ProteinRow | null,
  globalFilter: string,
  loading: boolean,
  failed: boolean,
  selectRow (row: ProteinRow | null): void,
  clearGlobalFilter (): void,
}

type KnownTargetImageType = {
  code: string,
  codeAscii: string,
  domain: string,
}

function KnownTargetImage({img}: {img: KnownTargetImageType}) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    getApiUrl(ProteinControllerApi, 'imageRaw', {codeAscii: img.codeAscii, topologicalDomain: img.domain}).then(setUrl);
  }, [img]);
  return (
    <img src={url} alt={`${img.code} - ${img.domain} domain`} />
  )
}

function KnownTargetTable({selected, globalFilter, clearGlobalFilter, ...props}: Props) {
  
  // Constants:
  const scrollElName = "knownTargetTable";
  const functionLength = 200;

  // State:
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (selected) {
      // selection changed (either by clicking on "Select protein" or "Load example"), therefore, expand row
      setExpandedRows({[selected.code]: true});
    } else {
      setExpandedRows({});
      clearGlobalFilter();
    }
  }, [selected, setExpandedRows, clearGlobalFilter]);

  const selectRow = useCallback((...args: Parameters<Props['selectRow']>): ReturnType<Props['selectRow']> => {
    props.selectRow(...args);
    scroller.scrollTo(scrollElName, {
      duration: 700,
      smooth: false,
      offset: -10,
    });
  }, [props.selectRow, scrollElName]);

  const notApplicable = <i>Not applicable</i>

  const rowExpansionTemplate = (data: ProteinRow) => {
    const images: KnownTargetImageType[] = data.proteins.flatMap(({ id, code, codeAscii, domains }) => (domains || []).map(domain => ({ proteinId: id, code, codeAscii, domain })));
    return <div className="ll-table-detail">
      <div className="ll-description">
        <p className="ll-function">
          <strong>Function:</strong> {data._function != null && data._function.length > 0 ? (
          <ShowMoreText text={data._function} size={functionLength} textRenderer={addPubmedLinksIntoPlainText} getProtectedRanges={pubmedLinkRanges} />
        ) : notApplicable}
        </p>
        {data.catalyticFunction != null && data.catalyticFunction.length > 0 ? <>
          <strong>Catalytic function:</strong>
          <ul style={{ paddingLeft: '1rem' }}>
            {data.catalyticFunction.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </> : (
          <p><strong>Catalytic function:</strong> {notApplicable}</p>
        )}
        <p className="ll-function">
          <strong>Regulation:</strong> {data.regulation != null && data.regulation.length > 0 ? (
          <ShowMoreText text={data.regulation} size={functionLength} textRenderer={addPubmedLinksIntoPlainText} getProtectedRanges={pubmedLinkRanges} />
        ) : notApplicable}
        </p>
        <p><strong>Length:</strong> {joinReactNodes(data.proteins.map(t => `${t.sequence.length} residues`))}{data.proteins.length > 1 && ` (${data.proteins.length} isoforms)`}</p>
      </div>
      <div className="ll-images">
        {images.length > 0 && <Galleria
            value={images}
            item={(img: KnownTargetImageType) => <KnownTargetImage img={img} /> }
            showThumbnails={false}
            caption={(img: KnownTargetImageType) => `${img.code} - ${img.domain} domain`}
            showIndicators={images.length > 1}
            autoPlay={images.length > 1}
            circular={images.length > 1}
        />}
      </div>
    </div>;
  }

  const onRowToggle = e => {
    setExpandedRows(e.data ?? {});
  }

  const uniprotBodyTemplate = (data: ProteinRow) => {
    // Showing the first uniprot accession, which is canonical.
    const uniprot = data.proteins[0].uniprot;
    return <ExtLink to={`https://www.uniprot.org/uniprot/${uniprot}`}>{uniprot}</ExtLink>
  }

  const selectBodyTemplate = (data: ProteinRow) => {
    const isSelected = selected !== null && data.code === selected.code;
    return (
      <Button
        size="sm"
        title={`Select ${data.code} as target protein`}
        onClick={() => selectRow(data)}
        disabled={isSelected}
      >
        {isSelected ? 'Selected' : 'Select protein'}
      </Button>
    );
  }

  const rowClassName = (data: ProteinRow) => (
    selected !== null ? {
      'p-highlight': data.code === selected.code,
    } : undefined
  )

  const emptyMessage = (
    props.failed    ? <div>{Messages.KNOWN_TARGETS_FAILED()}</div>
    : globalFilter  ? `No proteins matching “${globalFilter}”.`
    : props.loading ? (Messages.spinnerIcon)
    :                 "No proteins are available."
  );
  const dataRows = selected !== null ? [selected] : props.dataRows;
  return (
    <Element name={scrollElName}>
      <DataTable
        value={dataRows}
        dataKey="code"
        rowExpansionTemplate={rowExpansionTemplate}
        onRowToggle={onRowToggle}
        expandedRows={expandedRows}
        scrollable
        globalFilter={globalFilter}
        className={classNames(
          "p-datatable-sm p-datatable-striped ll-known-target-table",
          { "ll-selected": selected !== null }
        )}
        removableSort
        loading={props.loading}
        rowClassName={rowClassName}
        emptyMessage={emptyMessage}
      >
        <Column expander style={{ width: '3rem' }} />
        <Column field="code" header="Protein Code" sortable headerClassName="ll-header-cell" style={{ width: '15%' }} />
        <Column field="geneName" header="Gene Name" sortable headerClassName="ll-header-cell" style={{ width: '15%' }} bodyClassName="ll-gene-name" />
        <Column header="UniProtKB" sortable headerClassName="ll-header-cell" style={{ width: '15%' }} body={uniprotBodyTemplate} />
        <Column field="proteinName" header="Protein Name" sortable headerClassName="ll-header-cell" />
        <Column body={selectBodyTemplate} align="center" style={{ width: '15%' }} />
      </DataTable>
    </Element>
  )
}

export default KnownTargetTable;
